/**
 * Connecting Backlog from a screen that must not navigate (BRAI-689).
 *
 * The approval happens on Backlog's own domain, so it cannot be drawn inside
 * this app — and the screen that needs it most is a meeting that is recording,
 * where a route change unmounts the recorder and takes the recording with it.
 * So the approval gets a window of its own and the meeting is left alone.
 *
 * `rememberReturnPath` is deliberately not used with any of this. A window
 * opened from here inherits a copy of this tab's `sessionStorage`, so a
 * remembered meeting path would travel into the approval window and send *it*
 * to the meeting afterwards — a second, muted copy of the screen the reader is
 * already on.
 */

/** Named so a second press reuses the window rather than stacking another. */
const POPUP_NAME = 'backlog-connect'

const POPUP_FEATURES = 'popup=yes,width=520,height=720,noopener=no'

/** What the approval window posts back once the grant is stored. */
export const BACKLOG_CONNECTED_MESSAGE = 'backlog:connected'

/**
 * Open the approval beside the current screen.
 *
 * @param url Where Backlog wants the reader sent.
 * @returns The window, or null when the browser refused to open one — which
 *   is a popup blocker rather than an error, and has its own way out.
 */
export function openBacklogApproval(url: string): Window | null {
  return window.open(url, POPUP_NAME, POPUP_FEATURES)
}

/**
 * The same approval in an ordinary tab, for a browser that blocked the window.
 *
 * `noopener` is what makes this safe to fall back to: it gets a fresh browsing
 * context with no copy of this tab's `sessionStorage`, so the tab simply stays
 * on the settings screen once the grant is stored.
 *
 * @returns Whether a tab was opened; false means even this was blocked, and
 *   the reader has to be given the link.
 */
export function openBacklogApprovalTab(url: string): boolean {
  return window.open(url, '_blank', 'noopener') !== null
}

/**
 * Whether this document is an approval window this app opened.
 *
 * The settings screen uses it to decide whether to show itself or report back
 * and close. Every other way into settings arrives with no opener: the one
 * link that opens it in a tab carries `rel="noopener"`, and a typed or
 * bookmarked address has none by definition.
 */
export function isBacklogApprovalWindow(): boolean {
  try {
    return window.opener != null && window.opener !== window
  } catch {
    // A cross-origin opener can refuse the read. Not ours, then.
    return false
  }
}

/**
 * Tell the window that opened this one that the grant is stored.
 *
 * Best-effort on purpose. The screen that opened it also re-checks whenever it
 * regains focus, which is what actually guarantees the state catches up — this
 * only makes it immediate rather than a moment later.
 */
export function notifyBacklogOpener(): void {
  try {
    window.opener?.postMessage(BACKLOG_CONNECTED_MESSAGE, window.location.origin)
  } catch {
    // Ignored: see above.
  }
}
